import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, PLATFORM_ID, Renderer2, inject, signal } from '@angular/core';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import type { ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { FreeformHtml, FreeformHtmlCategory } from './freeform-html';

/**
 * Renders the CKEditor HTML of the Freeform HTML category attached to the block.
 * Shortcode placeholders are swapped for components by the `shortcodeRenderer`
 * wrapper TemplateSingleComponent puts around every block.
 */
@Component({
  selector: 'freeform-html',
  templateUrl: './freeform-html.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreeformHtmlComponent implements OnInit, AfterViewInit {
  // InsertableHostDirective assigns `data` directly on the instance, which a signal input() cannot receive.
  @Input() data: FreeformHtml;

  private readonly redactor = inject(WysiwygRedactorService);
  private readonly themeParser = inject(ThemeParserService);
  private readonly accessibilityService = inject(AccessibilityService);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly content = signal<string | null>(null);
  theme: ThemeInterface;

  private get category(): FreeformHtmlCategory | undefined {
    return this.data?.freeformHtmlCategory?.[0];
  }

  private get categoryContent(): string | null {
    return this.category?.freeformHtmlContent || null;
  }

  // Same fold cutoff as LayoutComponent: the first three blocks get LCP treatment.
  private get isBelowFold(): boolean {
    return (this.data?.index ?? 0) > 2;
  }

  private render(raw: string): void {
    this.content.set(this.redactor.bypassSanitizer(raw, this.isBelowFold ? 'lazy' : 'lcp'));
  }

  ngOnInit(): void {
    this.theme = this.themeParser.getThemeClasses({
      styles: this.data?.styles ?? [],
      columnCount: 0,
      columnDistribution: '',
    });

    const raw = this.categoryContent;
    if (!raw) return;
    // Server only, as LayoutComponent does: ngOnInit runs again on the client, which re-renders without hydration.
    if (!this.isBrowser && !this.isBelowFold) {
      const src = this.redactor.extractFirstImageSrc(raw);
      if (src) this.injectPreloadLink(src);
    }
    // The full redactor pass runs before the first render on both platforms, so raw shortcodes and
    // unformatted image URLs never reach the page. Until the nav links load, its aria labels fall
    // back to the link path.
    this.render(raw);
  }

  async ngAfterViewInit(): Promise<void> {
    const raw = this.categoryContent;
    // On the server, ShortcodeRendererDirective has already swapped the placeholders for components
    // and has no MutationObserver to do it again, so replacing the HTML would ship empty placeholders.
    if (!raw || !this.isBrowser) return;
    // Aria labels in bypassSanitizer read nav link text. The directive's MutationObserver
    // re-inserts the shortcode components into the replaced HTML.
    await this.accessibilityService.waitForNavLinks();
    this.render(raw);
  }

  private injectPreloadLink(src: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('href', src);
    this.renderer.appendChild(this.document.head, link);
  }
}
